import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { BookOpen, Download, Sparkles, PencilLine, Lock } from 'lucide-react';
import AcropolisZone from '../components/AcropolisZone';
import LiveCanvasTutor from '../components/LiveCanvasTutor';

const EXAMS = [
    {
        code: 'CBSE10',
        name: 'CBSE Class 10 Boards',
        country: 'India',
        date: 'Feb – Mar 2027',
        modules: ['Mathematics', 'Science', 'Social Science'],
        weight: [35, 35, 30],
        notes: 'Marginalia: NCERT is the north star — every marks-heavy question hides inside those pages.',
    },
    {
        code: 'JEE',
        name: 'Joint Entrance Exam',
        country: 'India',
        date: 'Jan · Apr 2027',
        modules: ['Physics', 'Chemistry', 'Mathematics'],
        weight: [30, 30, 40],
        notes: 'Marginalia: focus on rotational mechanics & organic reactions.',
    },
];

export default function ExamPrep() {
    const [open, setOpen] = useState(0);
    const [acropolis, setAcropolis] = useState(false);
    const [acropolisInitial, setAcropolisInitial] = useState(null);
    const [liveCanvas, setLiveCanvas] = useState(false);

    // Deep links from header Smart Search
    useEffect(() => {
        const onAcropolisOpen = (ev) => {
            setAcropolisInitial(ev.detail || null);
            setAcropolis(true);
        };
        const onExamOpen = (ev) => {
            const idx = EXAMS.findIndex((e) => e.code === ev.detail?.code);
            if (idx >= 0) setOpen(idx);
        };
        window.addEventListener('acropolis:open', onAcropolisOpen);
        window.addEventListener('exam:open', onExamOpen);
        // Consume pending deep links set before this section mounted (page-flip delay)
        try {
            const pendingAcro = sessionStorage.getItem('sf.deeplink.acropolis');
            if (pendingAcro) {
                sessionStorage.removeItem('sf.deeplink.acropolis');
                setAcropolisInitial(JSON.parse(pendingAcro));
                setAcropolis(true);
            }
            const pendingExam = sessionStorage.getItem('sf.deeplink.exam');
            if (pendingExam) {
                sessionStorage.removeItem('sf.deeplink.exam');
                const idx = EXAMS.findIndex((e) => e.code === pendingExam);
                if (idx >= 0) setOpen(idx);
            }
        } catch {}
        return () => {
            window.removeEventListener('acropolis:open', onAcropolisOpen);
            window.removeEventListener('exam:open', onExamOpen);
        };
    }, []);

    return (
        <div data-testid="section-exam-prep" className="space-y-10">
            <div className="flex items-start justify-between">
                <div className="mono text-[10px] text-[color:var(--moss-deep)]">§II — Exam Prep</div>
                <div className="mono text-[10px] text-[color:var(--moss-deep)]">2 syllabi indexed</div>
            </div>

            <h2 className="display text-[color:var(--ink)]" style={{ fontSize: 'clamp(40px, 5vw, 68px)' }}>
                Syllabi, <span className="display-italic" style={{ color: 'var(--lavender-deep)' }}>dissected</span>
                <br /> & annotated in the margins.
            </h2>

            {/* Acropolis CTA — distraction-free study zone */}
            <motion.div
                initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
                className="relative overflow-hidden rounded-lg border border-[color:var(--moss-deep)]/40 p-6 md:p-8"
                style={{
                    background: 'linear-gradient(135deg, var(--moss-deep) 0%, var(--moss-dark) 60%, #0B0F07 100%)',
                    boxShadow: '0 20px 45px -20px rgba(0,0,0,0.55), inset 0 0 60px rgba(183,156,255,0.08)',
                }}
                data-testid="acropolis-cta-card"
            >
                <div
                    aria-hidden
                    className="pointer-events-none absolute inset-0 opacity-30"
                    style={{
                        background:
                            'radial-gradient(60% 80% at 85% 20%, rgba(183,156,255,0.28) 0%, transparent 60%)',
                    }}
                />
                <div className="relative flex flex-col md:flex-row md:items-center gap-4 md:gap-8">
                    <div className="flex-1">
                        <div className="mono text-[10px] tracking-[0.28em] text-[color:var(--lavender)] flex items-center gap-1.5">
                            <Sparkles size={11} /> METIS-GUIDED · CBSE CLASS 10
                        </div>
                        <h3 className="display text-[color:var(--parchment)] mt-2" style={{ fontSize: 'clamp(26px, 3vw, 40px)', lineHeight: 1.05 }}>
                            Enter <span className="display-italic text-[color:var(--lavender)]">The Acropolis</span>.
                        </h3>
                        <p className="display-italic text-[color:var(--parchment)]/70 text-[15px] mt-2 max-w-lg">
                            A full-screen, distraction-free study zone. Metis walks you through NCERT chapters concept-by-concept,
                            then hands you a 10-minute speed test the moment you're ready.
                        </p>
                    </div>
                    <button
                        onClick={() => setAcropolis(true)}
                        data-testid="enter-acropolis"
                        className="btn-lavender px-6 py-3 rounded-sm mono text-xs whitespace-nowrap shadow-lg"
                    >
                        Enter The Acropolis →
                    </button>
                </div>
            </motion.div>

            {/* Live Canvas Tutor CTA — ₹500+ entitlement-gated */}
            <motion.div
                initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
                className="relative overflow-hidden rounded-lg border border-[color:var(--moss-deep)]/40 p-6 md:p-8"
                style={{
                    background: 'linear-gradient(135deg, #10160d 0%, #0A0F0C 55%, #050806 100%)',
                    boxShadow: '0 20px 45px -20px rgba(0,0,0,0.55), inset 0 0 60px rgba(183,156,255,0.06)',
                }}
                data-testid="live-canvas-cta-card"
            >
                <div className="relative flex flex-col md:flex-row md:items-center gap-4 md:gap-8">
                    <div className="flex-1">
                        <div className="mono text-[10px] tracking-[0.28em] text-[color:var(--lavender)] flex items-center gap-1.5">
                            <PencilLine size={11} /> LIVE CANVAS TUTOR
                            <span className="ml-2 inline-flex items-center gap-1 bg-[color:var(--lavender-deep)] text-white px-1.5 py-0.5 rounded-sm text-[8px] tracking-[0.1em]">
                                <Lock size={8} /> ₹500+ PLANS
                            </span>
                        </div>
                        <h3 className="display text-[color:var(--parchment)] mt-2" style={{ fontSize: 'clamp(24px, 2.6vw, 36px)', lineHeight: 1.05 }}>
                            Watch Metis <span className="display-italic text-[color:var(--lavender)]">solve math live</span> on the chalkboard.
                        </h3>
                        <p className="display-italic text-[color:var(--parchment)]/70 text-[15px] mt-2 max-w-lg">
                            Character-by-character equation solving with synchronized voice narration —
                            pause mid-step and ask "wait, why?" anytime.
                        </p>
                    </div>
                    <button
                        onClick={() => setLiveCanvas(true)}
                        data-testid="open-live-canvas"
                        className="btn-lavender px-6 py-3 rounded-sm mono text-xs whitespace-nowrap shadow-lg"
                    >
                        Open Live Canvas →
                    </button>
                </div>
            </motion.div>

            <div className="border-t border-[color:var(--moss-deep)]/30">
                {EXAMS.map((e, i) => {
                    const active = i === open;
                    return (
                        <div
                            key={e.code}
                            className="border-b border-[color:var(--moss-deep)]/30"
                            data-testid={`exam-row-${e.code}`}
                        >
                            <button
                                onClick={() => setOpen(active ? -1 : i)}
                                className="w-full flex items-center gap-6 py-5 text-left group"
                                data-testid={`exam-toggle-${e.code}`}
                            >
                                <span className="mono text-[10px] w-8 text-[color:var(--moss-deep)]">0{i + 1}</span>
                                <span
                                    className="display text-[color:var(--ink)] group-hover:italic transition-all"
                                    style={{ fontSize: 'clamp(28px, 3vw, 44px)' }}
                                >
                                    {e.name}
                                </span>
                                <span className="ml-auto mono text-[10px] text-[color:var(--moss-deep)]">
                                    {e.country} · {e.date}
                                </span>
                                <span
                                    className="ml-4 h-3 w-3 rounded-full border border-[color:var(--moss-deep)] transition-all"
                                    style={{ background: active ? 'var(--lavender)' : 'transparent' }}
                                />
                            </button>
                            <motion.div
                                initial={false}
                                animate={{ height: active ? 'auto' : 0, opacity: active ? 1 : 0 }}
                                transition={{ duration: 0.5, ease: [0.83, 0, 0.17, 1] }}
                                className="overflow-hidden"
                            >
                                <div className="pb-6 pl-14 pr-4 grid md:grid-cols-3 gap-6">
                                    <div className="md:col-span-2 space-y-4">
                                        <div className="space-y-2">
                                            {e.modules.map((m, mi) => (
                                                <div key={m} className="flex items-center gap-4">
                                                    <span className="mono text-[10px] w-6 text-[color:var(--moss-deep)]">
                                                        {String(mi + 1).padStart(2, '0')}
                                                    </span>
                                                    <span className="text-[color:var(--ink)] flex-1 text-sm">{m}</span>
                                                    <div className="w-40 h-[3px] bg-[color:var(--moss-deep)]/20 rounded-full overflow-hidden">
                                                        <motion.div
                                                            className="h-full bg-[color:var(--lavender-deep)]"
                                                            initial={{ width: 0 }}
                                                            animate={{ width: `${e.weight[mi]}%` }}
                                                            transition={{ duration: 0.9, ease: 'easeOut' }}
                                                        />
                                                    </div>
                                                    <span className="mono text-[10px] w-8 text-right text-[color:var(--moss-deep)]">
                                                        {e.weight[mi]}%
                                                    </span>
                                                </div>
                                            ))}
                                        </div>
                                        <div className="flex gap-2 pt-2">
                                            <button className="btn-lavender px-4 py-2 rounded-sm text-xs mono flex items-center gap-2" data-testid={`exam-download-${e.code}`}>
                                                <Download size={12} /> Download Folio
                                            </button>
                                            <button className="btn-ghost-moss px-4 py-2 rounded-sm text-xs mono flex items-center gap-2">
                                                <BookOpen size={12} /> Preview
                                            </button>
                                        </div>
                                    </div>
                                    <aside className="border-l border-[color:var(--moss-deep)]/40 pl-4 relative">
                                        <div className="mono text-[9px] text-[color:var(--moss-deep)] mb-2">MARGINALIA</div>
                                        <p className="display-italic text-[color:var(--ink)] text-[15px] leading-snug">
                                            {e.notes}
                                        </p>
                                        <div
                                            className="absolute -right-3 top-0 rotate-90 mono text-[9px] text-[color:var(--moss-deep)]"
                                            style={{ transformOrigin: 'right top' }}
                                        >
                                            §{e.code}
                                        </div>
                                    </aside>
                                </div>
                            </motion.div>
                        </div>
                    );
                })}
            </div>

            <AnimatePresence>
                {acropolis && (
                    <AcropolisZone
                        key="acropolis"
                        initialSubject={acropolisInitial?.subject}
                        initialChapter={acropolisInitial?.chapter}
                        onExit={() => { setAcropolis(false); setAcropolisInitial(null); }}
                    />
                )}
                {liveCanvas && (
                    <LiveCanvasTutor key="live-canvas" onExit={() => setLiveCanvas(false)} />
                )}
            </AnimatePresence>
        </div>
    );
}
